import { bgPrimary, bg0 } from '@osmosis/tailwind-ui/styles/background';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { Button } from '@osmosis/tailwind-ui/components/Button';

import React, { CSSProperties, useEffect, useRef } from 'react';

import Image from 'next/image';
import TopicsItem from './TopicsItem';
import { CLOUDFRONT_ASSET_URL } from '@osmosis/constants';

import { lighten } from '@osmosis/shared/styles/colorModifiers';
import { LinkObject, MenuTopic } from '../../types/topics';
import TopicsColumn from './TopicsColumn';
import PrimaryTopicsWrapper from './PrimaryTopicsWrapper';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import LockIcon from '../search/LockIcon';
import { useUserContext } from '../../context/UserContext';
import { faClose } from '@fortawesome/pro-solid-svg-icons/faClose';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { useContentSize } from '@osmosis/nextjs-components/context';

const BASIC_SCIENCES = 'Basic Sciences';

export type TopicsListProps = {
  topics: {
    primary: {
      groupName?: string;
      state?: string;
      navs: MenuTopic[];
    }[];
    footer: MenuTopic[];
  };
  containerSx?: CSSProperties;
  containerClassName?: string;
  dataTestId?: string;
  isVideoTopics?: boolean;
  /** Id for the Video Topics heading, so a parent dialog can label itself with it. */
  headingId?: string;
  onClose?: () => void;
  onSelectTopic?: (topic: LinkObject) => void;
};

const localizedGroupName = (intl: IntlShape): Record<string, string> => ({
  'Basic Sciences': intl.formatMessage({
    id: 'common.basicSciences',
    defaultMessage: 'Basic Sciences',
  }),
  'Clinical Sciences': intl.formatMessage({
    id: 'common.clinicalSciences',
    defaultMessage: 'Clinical Sciences',
  }),
});

/** Render the topics lists. */
const TopicsList = (props: TopicsListProps) => {
  const {
    topics,
    containerSx,
    containerClassName,
    dataTestId,
    isVideoTopics,
    headingId,
    onClose,
    onSelectTopic,
  } = props;
  const { primary, footer } = topics;
  const colors = useColors();
  const intl = useIntl();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const groupNamesList = localizedGroupName(intl);

  const { user } = useUserContext() || {};
  const subProducts = user?.subProducts;
  const isLockedFlag = user?.hasLockedContentFlag;

  let lockedSubProduct: string | undefined;
  if (subProducts && subProducts.some) {
    subProducts.some((subProduct) => {
      if (subProduct.state === 'locked') {
        lockedSubProduct = subProduct.groupName
          .replace(/ /g, '_')
          .toLowerCase();
      }

      return lockedSubProduct;
    });
  }

  const { contentWidth, isReady } = useContentSize(true);

  // Focus the close button when the video topics panel becomes visible
  useEffect(() => {
    if (isVideoTopics && closeBtnRef.current) {
      // small timeout lets screen readers announce the dialog first in some browsers
      setTimeout(() => closeBtnRef.current?.focus(), 0);
    }
  }, [isVideoTopics]);

  // Breakpoint where topic items will be dropdown list
  const isMobileView = isReady ? contentWidth <= 1140 : contentWidth <= 1024;

  return (
    <Flex
      data-testid="list-wrapper"
      style={{ maxHeight: 'calc(100vh - 285px)', ...containerSx }}
      className={`relative min-h-[350px] overflow-auto rounded-[10px] border border-gray-100 ${bg0} p-0 pt-0 lg:p-16 lg:pt-2 ${
        containerClassName || ''
      }`}
    >
      <Image
        height={340}
        width={92}
        src={`${CLOUDFRONT_ASSET_URL}4IgJhEZyTk62OLDkkAUdhlXXTQ2TvFBa.png`}
        className="absolute left-0 top-[24%] hidden md:block"
        alt=""
        priority={false}
      />

      <Image
        height={20}
        width={50}
        src={`${CLOUDFRONT_ASSET_URL}zsEVzQHVRRW5gaVMC8szQuOzTOC7Egvs.png`}
        className="absolute right-0 top-0 hidden md:block"
        alt=""
        priority={false}
      />

      <Image
        height={180}
        width={54}
        src={`${CLOUDFRONT_ASSET_URL}ojoC-pnrQuSAI79e7o2wCEU0Tei8kUUk.png`}
        className="absolute right-[30px] top-[30%] hidden md:block"
        alt=""
        priority={false}
      />

      <Image
        height={240}
        width={85}
        src={`${CLOUDFRONT_ASSET_URL}hve4ogdRQL6EaATD_fqwL98nQjuXbHGG.png`}
        className="absolute bottom-0 right-0 hidden md:block"
        alt=""
        priority={false}
      />

      <Box className={`p-4 px-10 md:px-20 lg:px-0`}>
        {isVideoTopics && (
          <Box>
            <Flex className={`mt-5 justify-between`}>
              <H4 id={headingId} color={colors.primaryText}>
                <FormattedMessage
                  id="topics.list.close-icon"
                  defaultMessage="Video Topics"
                />
              </H4>
              {/** Close button: programmatic focus ensures it receives focus first when the panel opens. */}
              <Button
                ref={closeBtnRef}
                className={`cursor-pointer rounded-md ${bg0} p-2`}
                onClick={() => onClose?.()}
                aria-label={intl.formatMessage({
                  id: 'topics.list.close_button_label',
                  defaultMessage: 'Close topics list',
                })}
                variant="transparent"
                data-testid="topics.list.close-button"
              >
                <Icon
                  data-testid="topics.list.close-icon"
                  icon={faClose}
                  color={colors.primaryText}
                  size="24px"
                  aria-hidden="true"
                />
              </Button>
            </Flex>
            <Box
              style={{
                borderBottom: `1px solid ${colors.primaryText}`,
              }}
              className={`mb-2 mt-4 w-full md:mb-8`}
            />
          </Box>
        )}

        <PrimaryTopicsWrapper isMobileView={isMobileView}>
          {primary.map((navGroup, index) => {
            const columns = navGroup.navs.reduce(
              (acc: MenuTopic[][], topicItem: MenuTopic) => {
                if (topicItem.stackable) {
                  acc[acc.length - 1].push(topicItem);
                } else {
                  acc.push([topicItem]);
                }

                return acc;
              },
              [],
            );

            const styles = {
              background:
                navGroup.groupName === BASIC_SCIENCES
                  ? colors.basicGroupBg
                  : colors.clinicalGroupBg,
              color:
                navGroup.groupName === BASIC_SCIENCES
                  ? colors.basicGroupText
                  : colors.clinicalGroupText,
            };

            return (
              <Flex
                key={index}
                style={{
                  margin: '0 auto',
                }}
                className={`mt-4 flex-col`}
              >
                {navGroup.groupName ? (
                  <Flex
                    data-testid="group-name-wrapper"
                    className={`relative mx-2 mt-8 h-5 flex-row items-center justify-center border-solid border-white md:mt-0 lg:mt-0 md:border-[${styles.background}] md:border-2 md:border-b-white`}
                  >
                    <Box
                      role="heading"
                      aria-level={5}
                      data-testid="group-name"
                      style={{
                        top: -20,

                        background: styles.background,
                        color: styles.color,
                      }}
                      className={`absolute w-full rounded-md p-2`}
                    >
                      {isLockedFlag &&
                      lockedSubProduct ===
                        navGroup.groupName.replace(/ /g, '_').toLowerCase() ? (
                        <LockIcon
                          variant={lockedSubProduct}
                          placement={'left'}
                        />
                      ) : null}
                      {groupNamesList[navGroup.groupName]}
                    </Box>
                  </Flex>
                ) : null}

                {!isMobileView ? (
                  <Flex className={`flex-nowrap justify-start`}>
                    {columns.map((col, i) => {
                      return (
                        <TopicsColumn
                          onSelectTopic={onSelectTopic}
                          key={`tc-${i}`}
                          menuTopics={col}
                        />
                      );
                    })}
                  </Flex>
                ) : (
                  <>
                    {columns.map((col, i) => {
                      return (
                        <TopicsColumn
                          onSelectTopic={onSelectTopic}
                          key={`tc-${i}`}
                          menuTopics={col}
                        />
                      );
                    })}
                  </>
                )}
              </Flex>
            );
          })}
        </PrimaryTopicsWrapper>

        {!!footer && (
          <>
            {isMobileView ? (
              <>
                <Box className={`w-full ${bgPrimary} mt-4 h-[0.5px]`} />

                {footer.map((item: MenuTopic, i: number, arr: MenuTopic[]) => {
                  return (
                    <TopicsItem
                      onSelectTopic={onSelectTopic}
                      headingLevel={5}
                      item={item}
                      isMobileView={isMobileView}
                      key={item.displayName}
                    />
                  );
                })}
              </>
            ) : (
              <Flex>
                {footer.map((item: MenuTopic, i: number, arr: MenuTopic[]) => {
                  return (
                    <Flex
                      data-testid={`${dataTestId}_block_${item.path}`}
                      key={item.path}
                      style={{
                        border: `1px solid ${lighten(0.5, colors.blue)}`,
                      }}
                      className={`flex-1 flex-nowrap justify-evenly ${bg0} mx-2 my-8 rounded-[3px] px-4 py-2`}
                    >
                      <TopicsItem
                        onSelectTopic={onSelectTopic}
                        headingLevel={5}
                        dataTestId={`${dataTestId}_block_${item.path}`}
                        item={item}
                        hasBgImage={i === arr.length - 1}
                      />
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
};

export default TopicsList;
